/**
 * Yakıt Yönetimi Y2 metinleri (ADR 0117 Ek Y2; ortağın tasarımı 01 Genel Bakış, 03 Yakıt Alımları,
 * 04 Transferler): ROB kartları, tank doluluk tablosu, son hareketler, alım ve hareket formları,
 * onay akışı, açılış bakiyesi ve zil. `fuel` ad alanına katılır.
 */

export const fuelLedgerEn = {
  overview: {
    vesselInfo: 'Vessel and Voyage Information',
    vessel: 'Vessel',
    currentVoyage: 'Current Voyage',
    departure: 'Departure Port',
    arrival: 'Arrival Port',
    etd: 'Estimated Departure',
    eta: 'Estimated Arrival',
    voyageStatus: 'Voyage Status',
    status: { planned: 'Planned', in_progress: 'In Progress' },
    noVoyage: 'No voyage',
    currentRob: 'Current ROB',
    totalCapacity: 'Total Capacity',
    capacityUnknown: 'Capacity needs density',
    dailyConsumption: 'Daily Consumption',
    dailyConsumptionOf: 'Daily Consumption ({{fuel}})',
    dailyConsumptionEmpty: 'No consumption posted from voyage reports in this period.',
    average: 'Average of last {{days}} days',
    perDay: '{{value}} MT/day',
    changeUp: 'Up {{value}}% on the average',
    changeDown: 'Down {{value}}% on the average',
    window: 'Period',
    windowOption: 'Last {{days}} days',
    chartAxis: 'Consumption (MT/day)',
    chartSeries: '{{fuel}} consumption',
    chartPoint: '{{day}}: {{value}} MT',
    snapshotDrift_one:
      'The ROB written in {{count}} submitted report differs from the ledger (a later approved record). The report is not rewritten; the ledger is the source.',
    snapshotDrift_other:
      'The ROB written in {{count}} submitted reports differs from the ledger (later approved records). The reports are not rewritten; the ledger is the source.',
    tankLevels: 'Tank Fill Status',
    columns: {
      tank: 'Tank Name',
      product: 'Product',
      robMt: 'Current Quantity (MT)',
      capacityMt: 'Capacity (MT)',
      fill: 'Fill Ratio',
    },
    other: 'Other',
    noTanks: 'No tanks defined.',
    consumptionChart: 'Fuel Consumption (Daily)',
    consumptionChartEmpty:
      'Daily consumption is drawn from submitted voyage reports; none in this period yet.',
    movements: 'Recent Fuel Movements',
    viewAll: 'View All',
    movementColumns: {
      at: 'Date / Time',
      type: 'Transaction Type',
      product: 'Product',
      mass: 'Quantity (MT)',
      route: 'Source → Target',
      description: 'Description',
      user: 'User',
    },
    movementTypes: {
      opening: 'Opening',
      bunker: 'Bunkering',
      transfer: 'Transfer',
      discharge: 'Discharge',
      return: 'Return',
      other_out: 'Other Out',
      consumption: 'Consumption',
      adjustment: 'Adjustment',
      reversal: 'Reversal',
    },
    noMovements: 'No approved fuel movements yet.',
    reconciliation: 'ROB Reconciliation',
    reconciliationEmpty:
      'No ROB reconciliation yet: the ledger opens with the approved opening balance; measurements are entered under ROB Reconciliation.',
    reconciliationAt: 'Compared at the latest measurement ({{at}}).',
    reconciliationNoSounding: 'No measurement since the voyage start: Ullage ROB is empty.',
    lastMeasured: 'Last measured {{value}} MT · {{at}}',
    createReport: 'Create ROB Reconciliation Report',
    noLedger:
      'The ROB ledger starts with the approved opening balance of each tank (Tanks ▸ Opening Balance).',
    pending_one: '{{count}} record awaiting approval is not included in the totals.',
    pending_other: '{{count}} records awaiting approval are not included in the totals.',
    negative: 'Negative ROB in {{tanks}}: review the movements.',
    volumeHint: 'Fill ratio by volume at 15 °C (MT ÷ density); capacity is the operating capacity.',
    percent: '{{value}}%',
  },
  records: {
    label: 'Record',
    new: 'New',
    empty: 'No records yet.',
    status: {
      draft: 'Draft',
      submitted: 'Submitted',
      returned: 'Returned',
      approved: 'Approved',
      corrected: 'Corrected',
      reversed: 'Reversed',
    },
    saveDraft: 'Save Draft',
    submit: 'Submit for Approval',
    approve: 'Approve',
    return: 'Return',
    correct: 'Correct',
    reverse: 'Reverse',
    discard: 'Discard Draft',
    cancel: 'Cancel',
    saved: 'Draft saved.',
    submitted: 'Sent for approval.',
    approved: 'Approved and posted to the ROB ledger.',
    returned: 'Returned to the sender.',
    reversed: 'Reversed; the ledger was corrected with reversal entries.',
    discarded: 'Draft discarded.',
    correctionOpened: 'Correction draft of {{no}} opened.',
    returnTitle: 'Return for correction',
    returnReason: 'Reason',
    returnHint: 'The record goes back to draft; the sender sees the reason in the bell.',
    reverseTitle: 'Reverse the approved record',
    reverseHint:
      'Reversal entries cancel the ledger lines of this record; the record stays in history and cannot be changed.',
    reverseReason: 'Reason',
    approveTitle: 'Approve {{no}}',
    approveHint: 'Approval posts the quantities to the ROB ledger in one transaction.',
    returnedBanner: 'Returned for correction by {{by}} ({{at}}): {{reason}}',
    correctionOf:
      'Correction of {{no}}: when approved, {{no}} is reversed and this record is posted.',
    correctedBanner: 'Corrected by a later record; its ledger lines are reversed.',
    reversedBanner: 'Reversed ({{at}}): {{reason}}',
    officeReadOnly: 'Vessel data: entered and approved on board; read only in the office.',
    noVessel: 'No vessel',
    loadError: 'The record could not be loaded.',
  },
  file: {
    choose: 'Choose file',
    remove: 'Remove {{name}}',
    none: 'No file',
    drop: 'Choose a file or drag it here',
    types: 'PDF, JPG, PNG (max. 25 MB)',
  },
  bunkering: {
    title: 'Bunkering Information',
    sections: {
      operation: 'Operation Information',
      supply: 'Supply and Document Information',
      fuel: 'Fuel and Quantity Information',
      distribution: 'Tank Distribution',
      sample: 'Sample, Analysis and Invoice Information',
      more: 'Additional Information',
    },
    fields: {
      port: 'Port',
      voyage: 'Voyage',
      unlinkedReason: 'Reason (no voyage)',
      start: 'Start Date / Time',
      end: 'End Date / Time',
      responsible: 'Responsible Officer',
      supplier: 'Supplier',
      barge: 'Barge / Tanker Name',
      bdnNo: 'BDN No',
      bdnDate: 'BDN Date',
      bdnFile: 'BDN (Bunker Delivery Note)',
      fuelType: 'Fuel Type',
      grade: 'Grade',
      density: 'Density (15°C)',
      sulphur: 'Sulphur (%)',
      bdnMt: 'BDN Quantity (MT)',
      shipMt: 'Ship Measurement (MT)',
      acceptedMt: 'Accepted Quantity (MT)',
      tankName: 'Tank Name',
      tankType: 'Tank Type',
      mass: 'Quantity (MT)',
      remarks: 'Description',
      sampleNo: 'Sample No',
      sealNo: 'Seal No',
      labReport: 'Analysis Report',
      unitPrice: 'Unit Price',
      currency: 'Currency',
      invoice: 'Invoice',
      deliveryMethod: 'Delivery Method',
      batchCode: 'Batch ID',
      viscosity: 'Viscosity (cSt)',
      flashPoint: 'Flash Point (°C)',
      sampleType: 'Sample Type',
      sampleLocation: 'Sample Storage',
      laboratory: 'Laboratory',
      analysisResult: 'Analysis Result',
      extraCost: 'Additional Cost',
      invoiceNo: 'Invoice No',
      fxRate: 'Exchange Rate',
      fxDate: 'Exchange Rate Date',
      differenceNote: 'Delivery Difference / Protest',
      altComponent: 'Alternative Fuel Component',
      altRatio: 'Ratio (%)',
      altOrigin: 'Origin',
      altCertificate: 'Certificate',
      generalRemarks: 'Remarks',
    },
    total: 'Total:',
    addTank: 'Add Tank',
    removeLine: 'Remove line {{n}}',
    lineLabel: 'Line {{n}}',
    bdnDifference: 'BDN − accepted: {{value}} MT (tracked separately; no consumption is created).',
    deliveryMethods: {
      barge: 'Barge',
      truck: 'Truck',
      pipeline: 'Pipeline',
      ship_to_ship: 'Ship to ship',
    },
    sampleTypes: { marpol: 'MARPOL', supplier: 'Supplier', ship: 'Ship', lab: 'Laboratory' },
  },
  transfers: {
    kindsLabel: 'Movement type',
    kinds: {
      transfer: 'Tank Transfer',
      discharge: 'Discharge',
      return: 'Return',
      fuel_change: 'Fuel Change',
      other_out: 'Other Out',
    },
    titles: {
      transfer: 'Fuel Transfer Record',
      discharge: 'Fuel Discharge Record',
      return: 'Fuel Return Record',
      fuel_change: 'Fuel Change Record',
      other_out: 'Other Fuel Out Record',
    },
    subtitles: {
      transfer: 'Record fuel transfers between the vessel’s tanks.',
      discharge: 'Record fuel discharged from a tank to a receiver ashore or afloat.',
      return: 'Record fuel returned to the supplier of a bunkering.',
      fuel_change: 'Record a fuel changeover of a consumer; stock does not change.',
      other_out: 'Record fuel leaving the stock other than by combustion.',
    },
    fields: {
      recordNo: 'Transfer No',
      movementNo: 'Movement No',
      start: 'Start Date / Time',
      end: 'End Date / Time',
      sourceTank: 'Source Tank',
      targetTank: 'Target Tank',
      method: 'Measurement Method',
      fuel: 'Fuel',
      mass: 'Quantity',
      responsible: 'Responsible Person',
      receiver: 'Received By',
      port: 'Port',
      bunkering: 'Related Bunkering',
      reason: 'Reason',
      equipment: 'Equipment',
      previousFuel: 'Previous Fuel',
      newFuel: 'New Fuel',
      sourceTanks: 'Source Tanks',
      description: 'Description',
      documents: 'Add Document',
    },
    methods: {
      tank_gauging: 'Tank Gauging',
      flowmeter: 'Flowmeter',
      bunker_meter: 'Bunker Meter',
      estimate: 'Estimate',
    },
    before: 'Before',
    after: 'After',
    check: 'Measurement Check',
    checkColumns: {
      tank: 'Tank',
      before: 'Before (MT)',
      after: 'After (MT)',
      diff: 'Difference (MT)',
    },
    sourceRow: 'Source Tank ({{name}})',
    targetRow: 'Target Tank ({{name}})',
    checkHint: 'Before and after are calculated from the approved ROB ledger.',
    notes: {
      transfer: {
        title: 'The vessel total ROB does not change.',
        body: 'This transfer does not change the vessel’s total fuel; it only changes the distribution between tanks.',
      },
      discharge: {
        title: 'The vessel total ROB decreases.',
        body: 'Discharged fuel leaves the stock; it is not an emission.',
      },
      return: {
        title: 'The vessel total ROB decreases.',
        body: 'Returned fuel reduces the batch of the related bunkering; it is not consumption.',
      },
      fuel_change: {
        title: 'Stock does not change.',
        body: 'A fuel change alone does not reduce stock; consumption is entered in voyage reports.',
      },
      other_out: {
        title: 'The vessel total ROB decreases.',
        body: 'Other out is not combustion; give the reason and attach the evidence.',
      },
    },
    warnings: {
      FUEL_NOT_ALLOWED_IN_TANK: 'The fuel is not in the allowed fuels of {{tank}}.',
      CAPACITY_EXCEEDED: '{{tank}} would exceed its operating capacity.',
      NEGATIVE_ROB: '{{tank}} would go below zero.',
    },
  },
  opening: {
    button: 'Opening Balance',
    title: 'Opening Balance — {{tank}}',
    hint: 'The ROB ledger of this tank starts with its approved opening balance. Earlier voyage reports are not posted to the ledger.',
    noTank: 'Save the tank first.',
    fields: {
      fuel: 'Fuel',
      measured: 'Date / Time',
      method: 'Measurement',
      volume: 'Volume (m³)',
      density: 'Density (15°C, t/m³)',
      mass: 'Quantity (MT)',
      basis: 'Basis',
      remarks: 'Remarks',
    },
    methods: {
      sounding: 'Sounding',
      ullage: 'Ullage',
      sensor: 'Sensor',
      bdn: 'BDN',
      other: 'Other',
    },
  },
  bell: {
    returned: 'Fuel record {{no}} returned to you',
    pending: 'Fuel record {{no}} awaits your approval',
  },
};

export const fuelLedgerErrorsEn = {
  FUEL_RECORD_LOCKED: 'A submitted or approved record cannot be changed; use Correct.',
  FUEL_RECORD_INCOMPLETE: 'Some required fields are missing or not consistent.',
  FUEL_RECORD_REVERSED: 'The record has been reversed.',
  FUEL_RECORD_NOT_APPROVED: 'Only an approved record can be corrected or reversed.',
  FUEL_RECORD_CORRECTED: 'The record already has a correction.',
  REVERSAL_REASON_REQUIRED: 'Enter the reason.',
  RETURN_REASON_REQUIRED: 'Enter the reason for the return.',
  CONTENT_CHANGED: 'The record changed meanwhile. Reload and try again.',
  ALREADY_SUBMITTED: 'The record has already been submitted.',
  NOT_PENDING: 'The record is not awaiting a decision.',
  NOT_APPROVER: 'Your role is not the approver of this step.',
  SELF_APPROVAL_FORBIDDEN: 'You cannot approve a record you entered or submitted.',
  SAME_APPROVER_FORBIDDEN: 'The same person cannot sign two steps.',
  NO_ELIGIBLE_APPROVER: 'No approver is on board for this record today.',
  APPROVAL_RULE_MISSING: 'The approval matrix has no rule for this record.',
  STALE_DECISION: 'The approval step changed meanwhile. Reload and try again.',
  INVALID_FUEL_RECORD: 'Some fields are not valid.',
  REAUTHENTICATION_REQUIRED: 'Enter your password to confirm.',
  SAME_TANK: 'Source and target tank cannot be the same.',
};

export const fuelLedgerFieldErrorsEn = {
  DISTRIBUTION_MISMATCH: 'The tank distribution must equal the accepted quantity.',
  DUPLICATE_TANK: 'Each tank can be used once.',
  END_BEFORE_START: 'Must be after the start.',
  FUTURE_DATE: 'Cannot be in the future.',
  SAME_TANK: 'Must differ from the source tank.',
  SAME_FUEL: 'Must differ from the previous fuel.',
  OPENING_EXISTS: 'This tank already has an opening balance.',
  INVALID_TIME: 'Enter a date and time.',
  INVALID_DATE: 'Enter a date.',
  INVALID_CURRENCY: 'Choose a currency.',
};

export const fuelLedgerTr: typeof fuelLedgerEn = {
  overview: {
    vesselInfo: 'Gemi ve Sefer Bilgileri',
    vessel: 'Gemi',
    currentVoyage: 'Mevcut Sefer',
    departure: 'Kalkış Limanı',
    arrival: 'Varış Limanı',
    etd: 'Tahmini Kalkış',
    eta: 'Tahmini Varış',
    voyageStatus: 'Sefer Durumu',
    status: { planned: 'Planlandı', in_progress: 'Devam Ediyor' },
    noVoyage: 'Sefer yok',
    currentRob: 'Güncel ROB',
    totalCapacity: 'Toplam Kapasite',
    capacityUnknown: 'Kapasite için yoğunluk gerekli',
    dailyConsumption: 'Günlük Tüketim',
    dailyConsumptionOf: 'Günlük Tüketim ({{fuel}})',
    dailyConsumptionEmpty: 'Bu dönemde sefer raporlarından işlenmiş tüketim yok.',
    average: 'Son {{days}} Gün Ortalaması',
    perDay: '{{value}} MT/gün',
    changeUp: 'Ortalamaya göre %{{value}} artış',
    changeDown: 'Ortalamaya göre %{{value}} azalış',
    window: 'Dönem',
    windowOption: 'Son {{days}} Gün',
    chartAxis: 'Tüketim (MT/gün)',
    chartSeries: '{{fuel}} Tüketimi',
    chartPoint: '{{day}}: {{value}} MT',
    snapshotDrift_one:
      'Gönderilmiş {{count}} raporun ROB değeri defterden farklı (sonradan onaylanan kayıt). Rapor yeniden yazılmaz; esas olan defterdir.',
    snapshotDrift_other:
      'Gönderilmiş {{count}} raporun ROB değeri defterden farklı (sonradan onaylanan kayıtlar). Raporlar yeniden yazılmaz; esas olan defterdir.',
    tankLevels: 'Tank Doluluk Durumu',
    columns: {
      tank: 'Tank Adı',
      product: 'Ürün',
      robMt: 'Mevcut Miktar (MT)',
      capacityMt: 'Kapasite (MT)',
      fill: 'Doluluk Oranı',
    },
    other: 'Diğer',
    noTanks: 'Tanımlı tank yok.',
    consumptionChart: 'Yakıt Tüketimi (Günlük)',
    consumptionChartEmpty:
      'Günlük tüketim gönderilmiş sefer raporlarından çizilir; bu dönemde henüz yok.',
    movements: 'Son Yakıt Hareketleri',
    viewAll: 'Tümünü Gör',
    movementColumns: {
      at: 'Tarih / Saat',
      type: 'İşlem Tipi',
      product: 'Ürün',
      mass: 'Miktar (MT)',
      route: 'Kaynak → Hedef',
      description: 'Açıklama',
      user: 'Kullanıcı',
    },
    movementTypes: {
      opening: 'Açılış',
      bunker: 'Alım',
      transfer: 'Transfer',
      discharge: 'Boşaltma',
      return: 'İade',
      other_out: 'Diğer Çıkış',
      consumption: 'Tüketim',
      adjustment: 'Düzeltme',
      reversal: 'Ters Kayıt',
    },
    noMovements: 'Henüz onaylı yakıt hareketi yok.',
    reconciliation: 'ROB Mutabakatı',
    reconciliationEmpty:
      'Henüz ROB mutabakatı yok: defter onaylı açılış bakiyesiyle başlar; ölçümler ROB Mutabakatı sekmesinde girilir.',
    reconciliationAt: 'Son ölçüm anında karşılaştırıldı ({{at}}).',
    reconciliationNoSounding: 'Sefer başından beri ölçüm yok: Ullage ROB boş.',
    lastMeasured: 'Son ölçüm {{value}} MT · {{at}}',
    createReport: 'ROB Mutabakat Raporu Oluştur',
    noLedger:
      'ROB defteri her tankın onaylı açılış bakiyesiyle başlar (Tanklar ▸ Açılış Bakiyesi).',
    pending_one: 'Onay bekleyen {{count}} kayıt toplamlara dahil değil.',
    pending_other: 'Onay bekleyen {{count}} kayıt toplamlara dahil değil.',
    negative: '{{tanks}} tankında negatif ROB: hareketleri inceleyin.',
    volumeHint: 'Doluluk 15 °C hacmine göre (MT ÷ yoğunluk); kapasite işletme kapasitesidir.',
    percent: '%{{value}}',
  },
  records: {
    label: 'Kayıt',
    new: 'Yeni',
    empty: 'Henüz kayıt yok.',
    status: {
      draft: 'Taslak',
      submitted: 'Gönderildi',
      returned: 'İade Edildi',
      approved: 'Onaylandı',
      corrected: 'Düzeltildi',
      reversed: 'Ters Kaydedildi',
    },
    saveDraft: 'Taslak Kaydet',
    submit: 'Onaya Gönder',
    approve: 'Onayla',
    return: 'İade Et',
    correct: 'Düzelt',
    reverse: 'Ters Kayıt',
    discard: 'Taslağı Sil',
    cancel: 'Vazgeç',
    saved: 'Taslak kaydedildi.',
    submitted: 'Onaya gönderildi.',
    approved: 'Onaylandı ve ROB defterine işlendi.',
    returned: 'Gönderene iade edildi.',
    reversed: 'Ters kaydedildi; defter ters kayıtla düzeltildi.',
    discarded: 'Taslak silindi.',
    correctionOpened: '{{no}} için düzeltme taslağı açıldı.',
    returnTitle: 'Düzeltmeye iade et',
    returnReason: 'Gerekçe',
    returnHint: 'Kayıt taslağa döner; gönderen gerekçeyi zilde görür.',
    reverseTitle: 'Onaylı kaydı ters çevir',
    reverseHint:
      'Ters kayıt bu kaydın defter satırlarını sıfırlar; kayıt geçmişte kalır ve değiştirilemez.',
    reverseReason: 'Gerekçe',
    approveTitle: '{{no}} onayı',
    approveHint: 'Onay, miktarları tek işlemle ROB defterine işler.',
    returnedBanner: '{{by}} düzeltmeye iade etti ({{at}}): {{reason}}',
    correctionOf:
      '{{no}} kaydının düzeltmesi: onaylanınca {{no}} ters kaydedilir, bu kayıt işlenir.',
    correctedBanner: 'Sonraki bir kayıtla düzeltildi; defter satırları ters kaydedildi.',
    reversedBanner: 'Ters kaydedildi ({{at}}): {{reason}}',
    officeReadOnly: 'Gemi verisi: gemide girilir ve onaylanır; ofiste salt okunur.',
    noVessel: 'Gemi yok',
    loadError: 'Kayıt okunamadı.',
  },
  file: {
    choose: 'Dosya seç',
    remove: '{{name}} dosyasını kaldır',
    none: 'Dosya yok',
    drop: 'Dosya seçin veya buraya sürükleyin',
    types: 'PDF, JPG, PNG (Maks. 25 MB)',
  },
  bunkering: {
    title: 'Yakıt Alımı Bilgileri',
    sections: {
      operation: 'Operasyon Bilgileri',
      supply: 'Tedarik ve Belge Bilgileri',
      fuel: 'Yakıt ve Miktar Bilgileri',
      distribution: 'Tank Dağılımı',
      sample: 'Numune, Analiz ve Fatura Bilgileri',
      more: 'Ek Bilgiler',
    },
    fields: {
      port: 'Liman',
      voyage: 'Voyage',
      unlinkedReason: 'Sefer yoksa açıklama',
      start: 'Başlangıç Tarih / Saat',
      end: 'Bitiş Tarih / Saat',
      responsible: 'Sorumlu Zabit',
      supplier: 'Tedarikçi',
      barge: 'Barç / Tanker Adı',
      bdnNo: 'BDN No',
      bdnDate: 'BDN Tarihi',
      bdnFile: 'BDN (Barge Delivery Note)',
      fuelType: 'Yakıt Türü',
      grade: 'Grade',
      density: 'Yoğunluk (15°C)',
      sulphur: 'Kükürt Oranı (%)',
      bdnMt: 'BDN Miktarı (MT)',
      shipMt: 'Gemi Ölçümü (MT)',
      acceptedMt: 'Kabul Miktarı (MT)',
      tankName: 'Tank Adı',
      tankType: 'Tank Tipi',
      mass: 'Miktar (MT)',
      remarks: 'Açıklama',
      sampleNo: 'Numune No',
      sealNo: 'Mühür No',
      labReport: 'Analiz Raporu',
      unitPrice: 'Birim Fiyat',
      currency: 'Para Birimi',
      invoice: 'Fatura',
      deliveryMethod: 'Teslimat Yöntemi',
      batchCode: 'Parti Kimliği',
      viscosity: 'Viskozite (cSt)',
      flashPoint: 'Parlama Noktası (°C)',
      sampleType: 'Numune Türü',
      sampleLocation: 'Numune Saklama Yeri',
      laboratory: 'Laboratuvar',
      analysisResult: 'Analiz Sonucu',
      extraCost: 'Ek Masraf',
      invoiceNo: 'Fatura No',
      fxRate: 'Kur',
      fxDate: 'Kur Tarihi',
      differenceNote: 'Teslimat Farkı / Protesto',
      altComponent: 'Alternatif Yakıt Bileşeni',
      altRatio: 'Oran (%)',
      altOrigin: 'Köken',
      altCertificate: 'Sertifika',
      generalRemarks: 'Açıklama',
    },
    total: 'Toplam:',
    addTank: 'Tank Ekle',
    removeLine: '{{n}}. satırı kaldır',
    lineLabel: '{{n}}. satır',
    bdnDifference: 'BDN − kabul: {{value}} MT (ayrı izlenir; tüketim üretilmez).',
    deliveryMethods: {
      barge: 'Barç',
      truck: 'Tanker kamyon',
      pipeline: 'Boru hattı',
      ship_to_ship: 'Gemiden gemiye',
    },
    sampleTypes: { marpol: 'MARPOL', supplier: 'Tedarikçi', ship: 'Gemi', lab: 'Laboratuvar' },
  },
  transfers: {
    kindsLabel: 'Hareket türü',
    kinds: {
      transfer: 'Tank Transferi',
      discharge: 'Boşaltma',
      return: 'İade',
      fuel_change: 'Yakıt Değişimi',
      other_out: 'Diğer Çıkış',
    },
    titles: {
      transfer: 'Yakıt Transferi Kaydı',
      discharge: 'Yakıt Boşaltma Kaydı',
      return: 'Yakıt İade Kaydı',
      fuel_change: 'Yakıt Değişimi Kaydı',
      other_out: 'Diğer Yakıt Çıkışı Kaydı',
    },
    subtitles: {
      transfer: 'Gemideki tanklar arasında yapılan yakıt transferlerini kaydedin.',
      discharge: 'Bir tanktan karaya ya da başka bir tekneye boşaltılan yakıtı kaydedin.',
      return: 'Bir yakıt alımının tedarikçisine iade edilen yakıtı kaydedin.',
      fuel_change: 'Bir tüketicinin yakıt değişimini kaydedin; stok değişmez.',
      other_out: 'Yanma dışında stoktan çıkan yakıtı kaydedin.',
    },
    fields: {
      recordNo: 'Transfer No',
      movementNo: 'Hareket No',
      start: 'Başlangıç Tarih / Saat',
      end: 'Bitiş Tarih / Saat',
      sourceTank: 'Kaynak Tank',
      targetTank: 'Hedef Tank',
      method: 'Ölçüm Yöntemi',
      fuel: 'Yakıt',
      mass: 'Miktar',
      responsible: 'Sorumlu Kişi',
      receiver: 'Teslim Alan',
      port: 'Liman',
      bunkering: 'İlgili Yakıt Alımı',
      reason: 'Neden',
      equipment: 'Ekipman',
      previousFuel: 'Önceki Yakıt',
      newFuel: 'Yeni Yakıt',
      sourceTanks: 'Kaynak Tanklar',
      description: 'Açıklama',
      documents: 'Belge Ekle',
    },
    methods: {
      tank_gauging: 'Tank Ölçümü',
      flowmeter: 'Akış Ölçer',
      bunker_meter: 'İkmal Sayacı',
      estimate: 'Tahmin',
    },
    before: 'Önce',
    after: 'Sonra',
    check: 'Ölçüm Kontrolü',
    checkColumns: { tank: 'Tank', before: 'Önce (MT)', after: 'Sonra (MT)', diff: 'Fark (MT)' },
    sourceRow: 'Kaynak Tank ({{name}})',
    targetRow: 'Hedef Tank ({{name}})',
    checkHint: 'Önce ve sonra onaylı ROB defterinden hesaplanır.',
    notes: {
      transfer: {
        title: 'Gemi toplam ROB’u değişmez.',
        body: 'Bu transfer geminin toplam yakıt miktarını etkilemez, sadece tanklar arası dağılımı değiştirir.',
      },
      discharge: {
        title: 'Gemi toplam ROB’u azalır.',
        body: 'Boşaltılan yakıt stoktan çıkar; emisyon sayılmaz.',
      },
      return: {
        title: 'Gemi toplam ROB’u azalır.',
        body: 'İade edilen yakıt ilgili alımın partisinden düşer; tüketim değildir.',
      },
      fuel_change: {
        title: 'Stok değişmez.',
        body: 'Yakıt değişimi tek başına stok düşürmez; tüketim sefer raporlarında girilir.',
      },
      other_out: {
        title: 'Gemi toplam ROB’u azalır.',
        body: 'Diğer çıkış yanma değildir; nedenini yazın ve kanıtını ekleyin.',
      },
    },
    warnings: {
      FUEL_NOT_ALLOWED_IN_TANK: 'Yakıt {{tank}} tankının izin verilen yakıtlarında değil.',
      CAPACITY_EXCEEDED: '{{tank}} işletme kapasitesini aşar.',
      NEGATIVE_ROB: '{{tank}} sıfırın altına düşer.',
    },
  },
  opening: {
    button: 'Açılış Bakiyesi',
    title: 'Açılış Bakiyesi — {{tank}}',
    hint: 'Bu tankın ROB defteri onaylı açılış bakiyesiyle başlar. Önceki sefer raporları deftere işlenmez.',
    noTank: 'Önce tankı kaydedin.',
    fields: {
      fuel: 'Yakıt',
      measured: 'Tarih / Saat',
      method: 'Ölçüm',
      volume: 'Hacim (m³)',
      density: 'Yoğunluk (15°C, t/m³)',
      mass: 'Miktar (MT)',
      basis: 'Dayanak',
      remarks: 'Açıklama',
    },
    methods: {
      sounding: 'Sounding',
      ullage: 'Ullage',
      sensor: 'Sensör',
      bdn: 'BDN',
      other: 'Diğer',
    },
  },
  bell: {
    returned: '{{no}} yakıt kaydı size iade edildi',
    pending: '{{no}} yakıt kaydı onayınızı bekliyor',
  },
};

export const fuelLedgerErrorsTr: typeof fuelLedgerErrorsEn = {
  FUEL_RECORD_LOCKED: 'Gönderilmiş ya da onaylanmış kayıt değiştirilemez; Düzelt’i kullanın.',
  FUEL_RECORD_INCOMPLETE: 'Bazı zorunlu alanlar eksik ya da tutarsız.',
  FUEL_RECORD_REVERSED: 'Kayıt ters kaydedilmiş.',
  FUEL_RECORD_NOT_APPROVED: 'Yalnız onaylı kayıt düzeltilir ya da ters kaydedilir.',
  FUEL_RECORD_CORRECTED: 'Kaydın zaten bir düzeltmesi var.',
  REVERSAL_REASON_REQUIRED: 'Gerekçeyi yazın.',
  RETURN_REASON_REQUIRED: 'İade gerekçesini yazın.',
  CONTENT_CHANGED: 'Kayıt bu arada değişti. Yenileyip tekrar deneyin.',
  ALREADY_SUBMITTED: 'Kayıt zaten gönderildi.',
  NOT_PENDING: 'Kayıt karar beklemiyor.',
  NOT_APPROVER: 'Rolünüz bu adımın onaylayıcısı değil.',
  SELF_APPROVAL_FORBIDDEN: 'Girdiğiniz ya da gönderdiğiniz kaydı onaylayamazsınız.',
  SAME_APPROVER_FORBIDDEN: 'Aynı kişi iki adımı imzalayamaz.',
  NO_ELIGIBLE_APPROVER: 'Bugün gemide bu kaydın onaylayıcısı yok.',
  APPROVAL_RULE_MISSING: 'Onay matrisinde bu kayıt için kural yok.',
  STALE_DECISION: 'Onay adımı bu arada değişti. Yenileyip tekrar deneyin.',
  INVALID_FUEL_RECORD: 'Bazı alanlar geçerli değil.',
  REAUTHENTICATION_REQUIRED: 'Onaylamak için parolanızı girin.',
  SAME_TANK: 'Kaynak ve hedef tank aynı olamaz.',
};

export const fuelLedgerFieldErrorsTr: typeof fuelLedgerFieldErrorsEn = {
  DISTRIBUTION_MISMATCH: 'Tank dağılımı kabul miktarına eşit olmalı.',
  DUPLICATE_TANK: 'Her tank bir kez kullanılır.',
  END_BEFORE_START: 'Başlangıçtan sonra olmalı.',
  FUTURE_DATE: 'Gelecekte olamaz.',
  SAME_TANK: 'Kaynak tanktan farklı olmalı.',
  SAME_FUEL: 'Önceki yakıttan farklı olmalı.',
  OPENING_EXISTS: 'Bu tankın açılış bakiyesi zaten var.',
  INVALID_TIME: 'Tarih ve saat girin.',
  INVALID_DATE: 'Tarih girin.',
  INVALID_CURRENCY: 'Para birimi seçin.',
};
